/**
 * WindowPicker — a single horizontal pill bar that lets the user
 * jump straight to any of Reclaim's "windows" (themed dashboards)
 * without scrolling the full homepage.
 *
 * Tapping a pill navigates to that window's landing route. The
 * user's last choice is remembered in localStorage so the
 * highlight feels persistent across sessions. The "Home" pill
 * always returns to /.
 *
 * Designed to live just under the hero on HomePage. Self-contained,
 * no external state, no API calls — safe to drop anywhere.
 */
import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Home, Crown, Target, Briefcase, Compass, Dumbbell, Sparkles,
} from 'lucide-react';

const WINDOWS = [
  { id: 'home',       label: 'Home',       to: '/',           icon: Home,      accent: '203,161,83' },
  { id: 'aurum',      label: 'Aurum',      to: '/aurum-edge', icon: Crown,     accent: '212,185,120' },
  { id: 'golf',       label: 'Golf',       to: '/golf',       icon: Target,    accent: '74,103,65'  },
  { id: 'networker',  label: 'Networker',  to: '/networker',  icon: Briefcase, accent: '102,164,123' },
  { id: 'inner_path', label: 'Inner Path', to: '/inner-path', icon: Compass,   accent: '212,185,120' },
  { id: 'gym',        label: 'Gym',        to: '/gym',        icon: Dumbbell,  accent: '180,90,90'  },
  { id: 'teen',       label: 'Teens',      to: '/teen',       icon: Sparkles,  accent: '230,120,170' },
];

const STORAGE_KEY = 'reclaim_window_choice';

const Pill = ({ w, active, darkContext, onTap }) => {
  const Icon = w.icon;
  const inactiveBg = darkContext
    ? 'rgba(255,255,255,0.04)'
    : 'rgba(26,29,26,0.05)';
  const inactiveBorder = darkContext
    ? '1px solid rgba(255,255,255,0.08)'
    : '1px solid rgba(26,29,26,0.10)';
  const inactiveColor = darkContext
    ? 'rgba(255,255,255,0.62)'
    : 'rgba(26,29,26,0.72)';
  const activeColor = darkContext ? '#F4EFE2' : '#1A1D1A';
  return (
    <motion.button
      whileTap={{ scale: 0.96 }}
      whileHover={{ y: -1 }}
      onClick={onTap}
      data-testid={`window-pill-${w.id}`}
      className="flex-shrink-0 inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full transition-all"
      style={{
        background: active
          ? `linear-gradient(145deg, rgba(${w.accent},0.30) 0%, rgba(${w.accent},0.10) 100%)`
          : inactiveBg,
        border: active
          ? `1px solid rgba(${w.accent},0.65)`
          : inactiveBorder,
        color: active ? activeColor : inactiveColor,
        boxShadow: active
          ? `0 0 18px rgba(${w.accent},0.18)`
          : 'none',
      }}
    >
      <Icon size={13} strokeWidth={1.8} />
      <span className="text-[12px] font-medium tracking-wide">{w.label}</span>
    </motion.button>
  );
};

export default function WindowPicker({ darkContext = true }) {
  const navigate = useNavigate();
  const location = useLocation();

  // Determine which pill is "active" purely from the current path.
  const activeId = (() => {
    const p = location.pathname;
    if (p.startsWith('/aurum-edge')) return 'aurum';
    if (p.startsWith('/golf')) return 'golf';
    if (p.startsWith('/networker')) return 'networker';
    if (p.startsWith('/inner-path')) return 'inner_path';
    if (p.startsWith('/gym')) return 'gym';
    if (p.startsWith('/teen')) return 'teen';
    if (p === '/' || p === '') return 'home';
    return null;
  })();

  const handleTap = (w) => {
    try { localStorage.setItem(STORAGE_KEY, w.id); } catch (_) { /* noop */ }
    navigate(w.to);
  };

  return (
    <div
      className="w-full"
      data-testid="window-picker"
    >
      <div className="flex items-center gap-1 mb-2">
        <span
          className="text-[10px] uppercase tracking-[0.32em] font-medium"
          style={{
            color: darkContext
              ? 'rgba(212,185,120,0.62)'
              : 'rgba(122,127,122,0.85)',
          }}
        >
          Your Windows
        </span>
        <div
          className="ml-2 flex-1 h-px"
          style={{
            background: darkContext
              ? 'linear-gradient(90deg, rgba(212,185,120,0.32) 0%, transparent 100%)'
              : 'linear-gradient(90deg, rgba(122,127,122,0.32) 0%, transparent 100%)',
          }}
        />
      </div>

      {/* Horizontal scrollable rail — works on mobile + desktop */}
      <div
        className="flex items-center gap-2 overflow-x-auto pb-1 -mx-1 px-1"
        style={{
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
        }}
      >
        <style>{`[data-testid="window-picker"] div::-webkit-scrollbar { display: none; }`}</style>
        {WINDOWS.map((w) => (
          <Pill
            key={w.id}
            w={w}
            active={activeId === w.id}
            darkContext={darkContext}
            onTap={() => handleTap(w)}
          />
        ))}
      </div>
    </div>
  );
}
